<template>
  <div
    class="PhoneInput"
    :class="{
      'is-error': hasError,
      'is-disabled': disabled,
      'is-static': static,
    }"
  >
    <div class="PhoneInput-fields">
      <!-- Country Code Autocomplete -->
      <Autocomplete
        v-model="prefix"
        :name="prefixFieldName"
        :options="countryOptions"
        :label="prefixLabel"
        :required="isRequired"
        :error="hasError"
        :disabled="disabled"
        :static="static"
        :placeholder="countryPlaceholder"
      />

      <!-- Hidden input for full phone autofill (1Password / mobile browsers fill autocomplete="tel" as one value) -->
      <input
        class="u-visually-hidden"
        type="tel"
        :name="props.name"
        autocomplete="tel"
        tabindex="-1"
        aria-hidden="true"
        @input="onFullPhoneAutofill"
      >

      <!-- Phone Number Input -->
      <Input
        v-model="number"
        :name="numberFieldName"
        :label="numberLabel"
        :required="isRequired"
        type="tel"
        :error="hasError"
        :disabled="disabled"
        :static="static"
        :placeholder="phonePlaceholder"
        autocomplete="off"
      />
    </div>

    <!-- Helper text only (errors are shown in inputs) -->
    <FieldHelper
      :errorMessage="errorMessage"
      :helperText="helperText"
    />
  </div>
</template>

<script setup lang="ts">
import { computed, watch, nextTick } from 'vue';
import { useI18n } from '@cvo/i18n/composables/useI18n';
import { getCountries, getCountryCallingCode, type CountryCode, parsePhoneNumberFromString } from 'libphonenumber-js';
import { useFieldValidation } from '@cvo/ui/composables';
import Autocomplete from '../Autocomplete/Autocomplete.vue';
import Input from '../../atoms/Input/Input.vue';
import FieldHelper from '../../atoms/FieldHelper/FieldHelper.vue';
import type { AutocompleteOption } from '../Autocomplete/Autocomplete.model';
import type { PhoneInputProps, PhoneChangeEvent } from './PhoneInput.model';

const props = withDefaults(defineProps<PhoneInputProps>(), {
  name: 'phone',
  error: undefined,
  disabled: false,
  defaultCountry: '',
});

const emit = defineEmits<{
  change: [event: PhoneChangeEvent];
  error: [error: unknown];
}>();

const { t } = useI18n();

// Computed properties for labels and placeholders with translation key defaults
const prefixLabel = computed(() => props.prefixLabel ?? t('ui.components.phone.prefix'));
const numberLabel = computed(() => props.numberLabel ?? t('ui.components.phone.number'));
const countryPlaceholder = computed(() => props.countryPlaceholder ?? t('ui.components.phone.countryPlaceholder'));
const phonePlaceholder = computed(() => props.phonePlaceholder ?? t('ui.components.phone.phonePlaceholder'));

// Support both object model and separate models for flexibility
// IMPORTANT: Don't use both at the same time - use either the unified model OR the separate models
const modelValue = defineModel<{ prefix?: string; number?: string }>();
const prefix = defineModel<string>('prefix', { default: '' });
const number = defineModel<string>('number', { default: '' });

// Use validation for the phone component if name is provided
const { hasError, errorMessage, isRequired } = useFieldValidation(props);

// Generate field names for validation
const prefixFieldName = computed(() => `${props.name}-prefix`);
const numberFieldName = computed(() => `${props.name}-number`);

const sanitizedNumber = computed<string>(() => {
  const validatedPhoneNumber = parsePhoneNumberFromString(`${prefix.value}${number.value}`);

  if (validatedPhoneNumber) return validatedPhoneNumber.nationalNumber;
  return number.value.replace(/[ -]/g, '');
});

// Sync unified model to separate models when unified model changes
watch(
  modelValue,
  (newValue) => {
    if (!newValue) return;
    const newPrefix = newValue.prefix ?? '';
    const newNumber = newValue.number ?? '';
    // Skip if this was triggered by our own sanitization update. Avoid infinite loop.
    if (newPrefix === prefix.value && newNumber === sanitizedNumber.value) return;
    prefix.value = newPrefix;
    number.value = newNumber;
  },
  { immediate: true, deep: true },
);

// Watch defaultCountry prop and set prefix accordingly
watch(
  () => props.defaultCountry,
  (newDefaultCountry) => {
    if (newDefaultCountry && !prefix.value) {
      try {
        const callingCode = getCountryCallingCode(newDefaultCountry as CountryCode);
        prefix.value = `+${callingCode}`;
      } catch (error) {
        console.warn(`Could not get calling code for ${newDefaultCountry}`);
        emit('error', error);
      }
    }
  },
  { immediate: true },
);

// Sync separate models back to unified model and emit change event (with sanitized number)
watch([prefix, number], async () => {
  modelValue.value = {
    prefix: prefix.value,
    number: sanitizedNumber.value,
  };

  await nextTick(); // ensure v-models are synced
  emit('change', {
    prefix: prefix.value,
    number: sanitizedNumber.value,
  });
});

function buildOptions(codes: string[]): AutocompleteOption[] {
  return codes
    .map((code) => {
      try {
        const callingCode = getCountryCallingCode(code as CountryCode);
        return {
          value: `+${callingCode}`,
          label: `(${code}) +${callingCode}`,
        };
      } catch (error) {
        console.warn(`Invalid country code: ${code}`);
        emit('error', error);
        return null;
      }
    })
    .filter((opt): opt is AutocompleteOption => opt !== null);
}

function onFullPhoneAutofill(event: Event) {
  const val = (event.target as HTMLInputElement).value;
  // 1Password sometimes omits the leading + (e.g. "33645454545" instead of "+33645454545")
  const parsed = parsePhoneNumberFromString(val) ?? parsePhoneNumberFromString(`+${val}`);
  if (parsed) {
    prefix.value = `+${parsed.countryCallingCode}`;
    number.value = parsed.nationalNumber;
  } else {
    number.value = val;
  }
}

const countryOptions = computed<AutocompleteOption[]>(() => {
  if (props.countries?.length) {
    const firstItem = props.countries[0];

    if (typeof firstItem === 'object' && 'value' in firstItem && 'label' in firstItem) {
      return props.countries as AutocompleteOption[];
    }

    return buildOptions(props.countries as string[]);
  }

  // Default: all countries
  return buildOptions(getCountries());
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;
@use '@cvo/ui/styles/utils/layout';
@use '@cvo/ui/styles/utils/text';
@use '@cvo/ui/styles/utils/responsive';

@layer cvo-ui {
  .PhoneInput {
    display: flex;
    flex-direction: column;
    gap: vars.$dimensions-d-2;

    &-fields {
      display: grid;
      grid-template-columns: 1fr; // Mobile: stacked
      gap: vars.$dimensions-d-4;

      @include responsive.tablet {
        grid-template-columns: 96px 1fr; // Desktop: minimal prefix | remaining for number
        gap: vars.$dimensions-d-3;
      }
    }

    &.is-disabled {
      opacity: 0.6;
      pointer-events: none;
    }
  }
}
</style>
